<template>
  <div class="claim-page">
    <div class="content-header">
      <div class="content-header-left mb-2">
        <h2 class="content-header-title">{{ $t('message.claim_request') || 'Claim Request' }}</h2>
        <p class="text-muted">{{ $t('message.claim_subtitle') || 'Submit a claim request for an existing policy' }}</p>
      </div>
    </div>

    <!-- STEP INDICATOR -->
    <div class="step-indicator mb-2">
      <div
        v-for="(step, i) in steps"
        :key="i"
        class="step-item"
        :class="{ active: currentStep === i + 1, completed: currentStep > i + 1 }"
      >
        <div class="step-circle">
          <font-awesome-icon v-if="currentStep > i + 1" icon="fa-solid fa-check" />
          <span v-else>{{ i + 1 }}</span>
        </div>
        <span class="step-label">{{ step }}</span>
      </div>
    </div>

    <!-- ===================== STEP 1: SEARCH ===================== -->
    <b-card v-if="currentStep === 1" class="shadow-sm">
      <b-card-title>
        <font-awesome-icon icon="fa-solid fa-magnifying-glass" class="mr-1 text-primary" />
        Find Your Policy
      </b-card-title>
      <b-card-text class="text-muted mb-2">
          Enter your policy number and email address to locate your policy (both can be found on the Validation Certificate). If your policy number includes a prefix (e.g. <strong>SFT2026 /</strong>), enter <strong>only the numbers after the slash</strong>.
      </b-card-text>

      <b-alert class="p-1" v-if="searchError" variant="danger" show dismissible @dismissed="searchError = null">
        {{ searchError }}
      </b-alert>

      <b-form @submit.prevent="searchPolicy">
        <b-row>
          <b-col cols="12" lg="5">
            <b-form-group label="Travel Insurance Policy Number *" label-for="input-quote-id">
              <b-input-group>
                <b-input-group-prepend is-text>
                  <font-awesome-icon icon="fa-solid fa-file-contract" />
                </b-input-group-prepend>
                <b-form-input
                  id="input-quote-id"
                  v-model="search.quote_id"
                  placeholder="e.g. 1234567890"
                  type="number"
                  required
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="5">
            <b-form-group label="Email Address *" label-for="input-email">
              <b-input-group>
                <b-input-group-prepend is-text>
                  <font-awesome-icon icon="fa-solid fa-envelope" />
                </b-input-group-prepend>
                <b-form-input
                  id="input-email"
                  v-model="search.email"
                  type="email"
                  placeholder="email@example.com"
                  required
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="2" class="d-flex align-items-end">
            <b-form-group class="w-100">
              <b-button type="submit" variant="primary" block :disabled="searching">
                <b-spinner v-if="searching" small class="mr-1" />
                <font-awesome-icon v-else icon="fa-solid fa-magnifying-glass" class="mr-1" />
                Search
              </b-button>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- ===================== STEP 2: POLICY DETAILS + FORM ===================== -->
    <div v-if="currentStep === 2">

      <!-- Policy Summary Card -->
      <b-card class="shadow-sm mb-2 policy-summary-card">
        <b-card-title>
          <font-awesome-icon icon="fa-solid fa-shield-halved" class="mr-1 text-success" />
          Policy Details
        </b-card-title>
        <b-row>
          <b-col cols="6" md="3">
            <div class="summary-item">
              <span class="summary-label">Travel Insurance Policy Number</span>
              <span class="summary-value">#{{ policy.quote_id }}</span>
            </div>
          </b-col>
          <b-col cols="6" md="3">
            <div class="summary-item">
              <span class="summary-label">Policy Type</span>
              <span class="summary-value">{{ policyName }}</span>
            </div>
          </b-col>
          <b-col cols="6" md="3">
            <div class="summary-item">
              <span class="summary-label">From</span>
              <span class="summary-value">{{ residenceName }}</span>
            </div>
          </b-col>
          <b-col cols="6" md="3">
            <div class="summary-item">
              <span class="summary-label">To</span>
              <span class="summary-value">{{ destinationName }}</span>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mt-1">
            <div class="summary-item">
              <span class="summary-label">Travel Dates</span>
              <span class="summary-value">{{ policy.start_date }} → {{ policy.end_date }}</span>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mt-1">
            <div class="summary-item">
              <span class="summary-label">Travellers</span>
              <span class="summary-value">{{ policy.number_of_customers || 1 }}</span>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mt-1">
            <div class="summary-item">
              <span class="summary-label">Total Amount</span>
              <span class="summary-value text-success font-weight-bold">
                €{{ policy.full_amount }}
              </span>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mt-1">
            <div class="summary-item">
              <span class="summary-label">Status</span>
              <b-badge :variant="stateVariant(policy.state)">
                {{ policy.state || 'Active' }}
              </b-badge>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mt-1">
            <div class="summary-item">
              <span class="summary-label">Policyholder</span>
              <span class="summary-value">{{ policy.email }}</span>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mt-1">
            <div class="summary-item">
              <span class="summary-label">Purchase Date</span>
              <span class="summary-value">{{ purchaseDate }}</span>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mt-1">
            <div class="summary-item">
              <span class="summary-label">Transaction ID</span>
              <span class="summary-value">{{ policy.transactionID || '—' }}</span>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Claim Form -->
      <b-card class="shadow-sm">
        <b-card-title>
          <font-awesome-icon icon="fa-solid fa-file-medical" class="mr-1 text-danger" />
          Claim Request
        </b-card-title>

        <b-alert variant="warning" show class="mb-2 mt-1 p-1">
          <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="mr-1" />
          Your claim will be reviewed by our team. We will contact you at <strong>{{ form.contact_email || policy.email }}</strong> within 48 hours.
        </b-alert>

        <b-alert v-if="submitError" variant="danger" show dismissible @dismissed="submitError = null">
          {{ submitError }}
        </b-alert>

        <b-form @submit.prevent="submitClaim">
          <b-row>

            <!-- Claim type dropdown -->
            <b-col cols="12" md="6">
              <b-form-group label-for="input-claim-type">
                <template #label>
                  Claim Type
                  <span class="text-danger">*</span>
                </template>
                <b-form-select
                  id="input-claim-type"
                  v-model="form.claim_type"
                  :options="claimTypeOptions"
                  required
                />
              </b-form-group>
            </b-col>

            <!-- Incident date -->
            <b-col cols="12" md="3">
              <b-form-group label-for="input-incident-date">
                <template #label>
                  Incident Date
                  <span class="text-danger">*</span>
                </template>
                <b-form-input
                  id="input-incident-date"
                  v-model="form.incident_date"
                  type="date"
                  :max="today"
                  required
                />
              </b-form-group>
            </b-col>

            <!-- Incident location -->
            <b-col cols="12" md="3">
              <b-form-group label-for="input-incident-location">
                <template #label>
                  Incident Location
                  <span class="text-danger">*</span>
                </template>
                <b-form-input
                  id="input-incident-location"
                  v-model="form.incident_location"
                  placeholder="City, Country"
                  required
                  minlength="8"
                />
              </b-form-group>
            </b-col>

            <!-- Description -->
            <b-col cols="12">
              <b-form-group label-for="input-description">
                <template #label>
                  Description of Incident
                  <span class="text-danger">*</span>
                </template>
                <b-form-textarea
                  id="input-description"
                  v-model="form.description"
                  placeholder="Please describe what happened, including any relevant details..."
                  rows="4"
                  required
                  minlength="50"
                />
                <small
                  :class="form.description.length >= 50 ? 'text-success' : 'text-danger'"
                >
                  {{ form.description.length }} characters
                  <span v-if="form.description.length < 50">
                    (minimum 50 required)
                  </span>
                  <span v-else>
                    ✓ Minimum reached
                  </span>
                </small>
              </b-form-group>
            </b-col>

             <!-- Contact email -->
            <b-col cols="12" md="6">
              <b-form-group label-for="input-contact-email">
              <template #label>
                Traveller Email for Claim Correspondence
                <span class="text-muted">(Optional — defaults to the policy email)</span>
              </template>
                <b-input-group>
                  <b-input-group-prepend is-text>
                    <font-awesome-icon icon="fa-solid fa-envelope-open-text" />
                  </b-input-group-prepend>
                  <b-form-input
                    id="input-contact-email"
                    v-model="form.contact_email"
                    type="email"
                    :placeholder="policy.email"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>

            <!-- Declaration -->
            <b-col cols="12">
              <div class="border rounded p-2 bg-warning-light mb-2">
                <b-form-checkbox
                  v-model="form.declaration"
                  :value="true"
                  :unchecked-value="false"
                  required
                >
                  I confirm that I am the policyholder or an authorised representative and that the information provided is true and accurate to the best of my knowledge. I understand that this claim request will be reviewed by the Safetravel.today team and that submitting this form does not guarantee approval or payment of the claim.
                </b-form-checkbox>
              </div>
            </b-col>

          </b-row>

          <div class="d-flex justify-content-between mt-1">
            <b-button variant="outline-secondary" @click="resetSearch">
              <font-awesome-icon icon="fa-solid fa-arrow-left" class="mr-1" />
              Back
            </b-button>
            <b-button
              type="submit"
              variant="danger"
              :disabled="submitting || !isClaimValid"
            >
              <b-spinner v-if="submitting" small class="mr-1" />
              <font-awesome-icon v-else icon="fa-solid fa-paper-plane" class="mr-1" />
              Submit Claim
            </b-button>
          </div>
        </b-form>
      </b-card>
    </div>

    <!-- ===================== STEP 3: CONFIRMATION ===================== -->
    <b-card v-if="currentStep === 3" class="shadow-sm text-center py-3">
      <div class="confirmation-icon mb-2">
        <font-awesome-icon icon="fa-solid fa-circle-check" class="text-success" style="font-size: 64px;" />
      </div>
      <h3 class="text-success mb-1">Claim Submitted Successfully</h3>
      <p class="text-muted mb-2">
        Your claim request for policy <strong>#{{ policy.quote_id }}</strong> has been received.<br>
        Our team will review it and contact you at <strong>{{ policy.email }}</strong> within 48 hours.
      </p>

      <b-card class="text-left d-inline-block mt-1 mb-2 confirmation-summary" bg-variant="light">
        <div class="summary-item mb-1">
          <span class="summary-label">Travel Insurance Policy Number:</span>
          <span class="summary-value ml-1">#{{ policy.quote_id }}</span>
        </div>
        <div class="summary-item mb-1">
          <span class="summary-label">Claim Type:</span>
          <span class="summary-value ml-1">{{ selectedClaimTypeLabel }}</span>
        </div>
        <div class="summary-item mb-1" v-if="form.incident_date">
          <span class="summary-label">Incident Date:</span>
          <span class="summary-value ml-1">{{ form.incident_date }}</span>
        </div>
        <div class="summary-item mb-1" v-if="form.incident_location">
          <span class="summary-label">Incident Location:</span>
          <span class="summary-value ml-1">{{ form.incident_location }}</span>
        </div>
        <div class="summary-item" v-if="form.description">
          <span class="summary-label">Description:</span>
          <span class="summary-value ml-1">{{ form.description }}</span>
        </div>
        <div class="summary-item mb-1" v-if="form.contact_email">
          <span class="summary-label">Contact Email:</span>
          <span class="summary-value ml-1">{{ form.contact_email }}</span>
        </div>
      </b-card>

      <div class="border rounded p-2 bg-light mb-2 text-left">
        <p class="mb-0 text-muted small">
          <font-awesome-icon icon="fa-solid fa-circle-info" class="mr-1" />
          For urgent matters, contact us at
          <a href="mailto:customer-support@safetravel.today">customer-support@safetravel.today</a>
          or call <strong>+30 210 7007135</strong> (Mon–Fri, 9:00–17:00).
        </p>
      </div>

      <b-button variant="primary" @click="resetAll">
        <font-awesome-icon icon="fa-solid fa-rotate-left" class="mr-1" />
        Submit Another Claim
      </b-button>
    </b-card>

  </div>
</template>

<script>
import {
  BCard, BCardTitle, BCardText,
  BRow, BCol,
  BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea,
  BFormCheckbox,
  BButton, BSpinner,
  BAlert, BBadge,
  BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue'
import { countrysFrom, countrysTo, products } from '@/products';

export default {
  name: 'ClaimRequest',
  components: {
    BCard, BCardTitle, BCardText,
    BRow, BCol,
    BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea,
    BFormCheckbox,
    BButton, BSpinner,
    BAlert, BBadge,
    BInputGroup, BInputGroupPrepend,
  },
  data() {
    return {
      currentStep: 1,
      steps: ['Find Policy', 'Claim Request', 'Confirmation'],

      // Search
      search: { quote_id: '', email: '' },
      searching: false,
      searchError: null,

      // Policy found
      policy: {},

      // Claim form
      form: {
        claim_type: null,
        incident_date: null,
        incident_location: '',
        description: '',
        contact_email: '',
        declaration: false,
      },
      submitting: false,
      submitError: null,
      claimTypeOptions: [
        { value: null, text: '— Select a claim type —', disabled: true },
        { value: 'medical_expenses', text: 'Emergency Medical, Repatriation & Other Expenses (incl. Dental)' },
        { value: 'hospital_confinement', text: 'Hospital Confinement Benefit' },
        { value: 'trip_cancellation', text: 'Trip Cancellation / Curtailment' },
        { value: 'personal_accident', text: 'Personal Accident' },
        { value: 'baggage_loss_damage', text: 'Baggage Loss or Damage' },
        { value: 'baggage_delay', text: 'Baggage Delay' },
        { value: 'personal_money_documents', text: 'Personal Money, Passport & Documents' },
        { value: 'personal_liability', text: 'Personal Liability' },
        { value: 'travel_delay', text: 'Travel Delay' },
        { value: 'missed_departure', text: 'Missed Departure' },
        { value: 'legal_expenses', text: 'Legal Expenses & Assistance' },
        { value: 'other', text: 'Other (please describe)' },
      ],
    }
  },
  computed: {
    token_id() {
      return this.$store.state.app?.userData?.id ?? null
    },
    purchaseDate() {
      if (!this.policy.r_timestamp) return '—'
      return new Date(this.policy.r_timestamp).toLocaleDateString()
    },
    residenceName() {
      return (
        countrysFrom.find(c => c.id == this.policy.residence)?.text || '—'
      )
    },
    destinationName() {
      return (
        countrysTo.find(c => c.id == this.policy.destination)?.text || '—'
      )
    },
    today() {
      return new Date().toISOString().split('T')[0]
    },
    selectedClaimTypeLabel() {
      const found = this.claimTypeOptions.find(o => o.value === this.form.claim_type)
      return found ? found.text : '—'
    },
    policyName() {
        return products[this.policy.policyType] || this.policy.policyType || '—'
    },
    isClaimValid() {
      return (
        this.form.declaration &&
        this.form.claim_type &&
        this.form.incident_date &&
        this.form.incident_location.length >= 8 &&
        this.form.description.length >= 50
      )
    }
  },
  methods: {
    stateVariant(state) {
      const map = {
        New: 'info',
        Active: 'success',
        Cancelled: 'danger',
        Expired: 'secondary',
        Pending: 'warning',
      }
      return map[state] || 'secondary'
    },

    async searchPolicy() {
      this.searching = true
      this.searchError = null
      try {
        const response = await fetch('https://api-quote.safetravel.today/v2/app/get_claim_policy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: this.token_id,
            jwt: window.$cookies.get('jwt') ?? null,
            quote_id: this.search.quote_id,
            email: this.search.email,
          }),
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        if (data.error) throw new Error(data.error)
        if (!data || !data.quote_id) throw new Error('Policy not found. Please check your details.')
        this.policy = data
        this.currentStep = 2
      } catch (err) {
        this.searchError = err.message || 'Policy not found. Please check your Travel Insurance Policy Number and email.'
      } finally {
        this.searching = false
      }
    },

    async submitClaim() {
      this.submitting = true
      this.submitError = null
      try {
        const payload = {
          token: this.token_id,
          jwt: window.$cookies.get('jwt') ?? null,
          quote_id:           this.policy.quote_id,
          email:              this.policy.email,
          policyType:         this.policy.policyType,
          start_date:         this.policy.start_date,
          end_date:           this.policy.end_date,
          full_amount:        this.policy.full_amount,
          claim_type:         this.form.claim_type,
          incident_date:      this.form.incident_date || null,
          incident_location:  this.form.incident_location || null,
          description:        this.form.description || null,
          contact_email:      this.form.contact_email || this.policy.email
        }
        const response = await fetch('https://api-quote.safetravel.today/v2/app/submit_claim', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        if (data.error) throw new Error(data.error)
        this.currentStep = 3
      } catch (err) {
        this.submitError = err.message || 'Failed to submit claim. Please try again or contact support.'
      } finally {
        this.submitting = false
      }
    },

    resetSearch() {
      this.currentStep = 1
      this.policy = {}
      this.searchError = null
      this.submitError = null
    },

    resetAll() {
      this.resetSearch()
      this.search = { quote_id: '', email: '' }
      this.form = { claim_type: null, incident_date: null, incident_location: '', description: '', contact_email: '', declaration: false }
    },
  },
}
</script>

<style scoped>
.step-indicator {
  display: flex;
  align-items: center;
}
.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}
.step-item:not(:last-child)::after {
  content: '';
  flex: 1;
  height: 2px;
  background: #dee2e6;
  margin: 0 8px;
}
.step-item.completed:not(:last-child)::after {
  background: #28a745;
}
.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #dee2e6;
  color: #5d8191;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 13px;
  flex-shrink: 0;
  transition: all 0.3s;
}
.step-item.active .step-circle {
  background: #5d8191;
  color: white;
}
.step-item.completed .step-circle {
  background: #28a745;
  color: white;
}
.step-label {
  font-size: 13px;
  color: #5d8191;
  white-space: nowrap;
}
.step-item.active .step-label {
  color: #5d8191;
  font-weight: 600;
}
.step-item.completed .step-label {
  color: #28a745;
}
.policy-summary-card {
  border-left: 4px solid #28a745;
}
.summary-item {
  display: flex;
  flex-direction: column;
}
.summary-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #5d8191;
  letter-spacing: 0.5px;
}
.summary-value {
  font-size: 14px;
  font-weight: 600;
  color: #2c2c2c;
}
.confirmation-icon {
  animation: pop-in 0.4s ease;
}
@keyframes pop-in {
  0% { transform: scale(0.5); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}
.confirmation-summary {
  min-width: 300px;
}
.bg-warning-light {
  background-color: rgba(255, 193, 7, 0.15);
}
@media (max-width: 600px) {
  .step-indicator {
    flex-wrap: wrap;
    row-gap: 10px;
  }
  .step-item {
    flex: 0 0 48%;
  }
  .step-item:not(:last-child)::after {
    content: none;
  }
  .step-item:last-child {
    flex: 0 0 100%;
    justify-content: flex-start;
  }
}
</style>